import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { RunDetail, executionStatsService, isAbort } from '../../services/executionStatsService';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { PanelError } from '../Benchmark/BenchmarkPrimitives';
import { BUCKET_LABELS, bucketDefinition, relativeTime } from './executionStatsVocabulary';
import { StatusPill, elapsed, when } from './runPresentation';
import RunStepTimeline from './RunStepTimeline';
import { baseCode } from './runLogLinks';
import { dsVar, ink } from '../../theme/tokens';

const heading = {
    fontSize: '0.6875rem', fontWeight: 700, color: dsVar('fg-2'), textTransform: 'uppercase' as const,
    letterSpacing: '0.06em', m: 0, mb: 1
};

const Field: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({ label, children, mono }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>{label}</Typography>
        <Typography
            component="div"
            sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}
        >
            {children}
        </Typography>
    </Box>
);

const CopyReference: React.FC<{ value: string }> = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        }).catch(() => undefined);
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{value}</Box>
            <Tooltip title={copied ? 'Copied' : 'Copy reference ID'} arrow>
                <IconButton size="small" onClick={copy} aria-label="Copy reference ID" sx={{ color: dsVar('fg-muted'), p: 0.25 }}>
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

interface RunDetailDrawerProps {
    /** The run to show; the drawer is closed without one. */
    referenceId: string | null;
    onClose: () => void;
    /** Lists every run of this run's workflow. */
    onShowWorkflow?: (code: string) => void;
    /** Lists every run in this run's campaign. */
    onShowCampaign?: (campaignId: string) => void;
}

/**
 * One run: its outcome, the step it failed or stopped at, and where it ran.
 *
 * Docked like RunListDrawer, so the run log stays on screen and the next row
 * can be opened without closing this one. Styled with sx: it's portalled.
 */
const RunDetailDrawer: React.FC<RunDetailDrawerProps> = ({ referenceId, onClose, onShowWorkflow, onShowCampaign }) => {
    const [detail, setDetail] = useState<RunDetail | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        setDetail(null);
        setError(null);
        if (!referenceId) return;
        const controller = new AbortController();
        executionStatsService
            .getRun(referenceId, { signal: controller.signal })
            .then(setDetail)
            .catch((err) => { if (!isAbort(err)) setError(err?.message || 'Could not load this run.'); });
        return () => controller.abort();
    }, [referenceId, attempt]);

    const run = detail?.run;
    const context = run
        ? [run.context.industry, run.context.geography, run.context.language].filter((v) => v && v !== 'NA')
        : [];

    return (
        <EntityDetailDrawer
            open={Boolean(referenceId)}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<ReceiptLongIcon />}
            subtitle={run?.bucket ? `${BUCKET_LABELS[run.bucket]} run` : 'Run'}
            title={run?.name || run?.workflowName || referenceId || ''}
            description=""
            actions={run && (onShowWorkflow || onShowCampaign) ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {onShowWorkflow && run.code && (
                        <Button size="small" variant="outlined" onClick={() => onShowWorkflow(baseCode(run.code as string))}
                            sx={{ color: dsVar('accent-fg'), borderColor: dsVar('accent-fg'), textTransform: 'none' }}>
                            All runs of this workflow
                        </Button>
                    )}
                    {onShowCampaign && run.campaignId && (
                        <Button size="small" variant="outlined" onClick={() => onShowCampaign(run.campaignId as string)}
                            sx={{ color: dsVar('fg-soft'), borderColor: dsVar('line'), textTransform: 'none' }}>
                            All runs in this campaign
                        </Button>
                    )}
                </Box>
            ) : undefined}
        >
            {error && <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />}

            {!error && !detail && referenceId && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>Loading run…</Typography>
            )}

            {run && detail && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Tooltip title={run.bucket ? bucketDefinition(run.bucket, detail.staleAfterHours) : ''} arrow>
                            <span><StatusPill bucket={run.bucket} status={run.status} /></span>
                        </Tooltip>
                        <Tooltip title="Reload this run" arrow>
                            <IconButton size="small" onClick={() => setAttempt((n) => n + 1)} aria-label="Reload run" sx={{ color: dsVar('fg-muted') }}>
                                <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <Field label="Started">
                            <span title={run.startedAt ?? undefined}>{when(run.startedAt)}</span>
                            <Box component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.6875rem' }}> · {relativeTime(run.startedAt)}</Box>
                        </Field>
                        <Field label="Duration">{elapsed(run)}</Field>
                        <Field label="Workflow">
                            {run.workflowName || '—'}
                            {run.code && (
                                <Box sx={{ fontSize: '0.625rem', color: dsVar('fg-muted'), fontFamily: '"JetBrains Mono", monospace' }}>{run.code}</Box>
                            )}
                        </Field>
                        <Field label="Campaign" mono>{run.campaignId || '—'}</Field>
                    </Box>

                    <Field label="Reference ID">{run.referenceId && <CopyReference value={run.referenceId} />}</Field>

                    {run.errorMessage && (
                        <Box sx={{ p: 1.25, borderRadius: '8px', border: '1px solid rgba(208, 59, 59, 0.4)', backgroundColor: 'rgba(208, 59, 59, 0.08)' }}>
                            <Typography sx={{ fontSize: '0.6875rem', color: ink('#F87171'), fontWeight: 700, mb: 0.5 }}>Error</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: ink('#FCA5A5'), fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {run.errorMessage}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <Typography component="h4" sx={heading}>Steps</Typography>
                        <RunStepTimeline detail={detail} />
                    </Box>

                    {context.length > 0 && (
                        <Box>
                            <Typography component="h4" sx={heading}>Run context</Typography>
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>{context.join(' · ')}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </EntityDetailDrawer>
    );
};

export default RunDetailDrawer;
